import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import LeadDetailSheet from "@/components/LeadDetailSheet";
import { ChevronRight, Link2, MapPin } from "lucide-react";
import { SCHEDULED_ACTIVITY_TYPES, flattenPendingActivities } from "@/lib/scheduledActivities";
import CollapsibleWidgetList from "@/components/ui/collapsible-widget-list";

// Dashboard counterpart to FollowUpTodayWidget, but for the full Aktivitas Terjadwal
// board (Follow Up/Survei/Zoom Meeting), showing everything pending in the next 14 days —
// not just what's due today. FollowUpTodayWidget stays focused on "due today, act now";
// this one is the broader upcoming-schedule view, and both can include a Follow Up entry
// for the same lead without it being duplicate/confusing — one is a same-day nudge, the
// other is forward planning. The two never diverge on the underlying date because the
// backend keeps lead.follow_up_date synced to whichever pending follow_up-type entry here
// is soonest (see _sync_lead_followup_date in server.py). Reuses the same ["leads"] query
// cache (no extra request) since enrich_lead already returns scheduled_activities per lead.
export default function ScheduledActivitiesWidget() {
  const [selected, setSelected] = useState(null);
  const { data: leads = [] } = useQuery({ queryKey: ["leads"], queryFn: async () => (await api.get("/leads")).data });
  const all = flattenPendingActivities(leads);
  const now = Date.now();
  // Overdue + due-today always shown; upcoming capped to the next 14 days so the widget
  // doesn't fill up with a Zoom meeting scheduled 3 months out.
  const horizon = now + 14 * 86400000;
  const due = all.filter((a) => new Date(a.scheduled_at).getTime() <= horizon);

  return (
    <div className="card-surface rounded-2xl p-5 mb-4" data-testid="scheduled-activities-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-semibold flex items-center gap-2">
          <span className="text-purple-400">📅</span>Jadwal Aktivitas (Follow Up/Survei/Zoom)
        </h3>
        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400" data-testid="scheduled-activities-count">{due.length}</span>
      </div>
      {due.length === 0 ? (
        <div className="text-center text-zinc-600 text-sm py-6">Tidak ada jadwal aktivitas dalam 14 hari ke depan</div>
      ) : (
        <CollapsibleWidgetList testId="scheduled-activities" items={due} renderItem={(a) => {
          const meta = SCHEDULED_ACTIVITY_TYPES[a.type];
          const Icon = meta?.icon;
          const d = new Date(a.scheduled_at);
          const overdue = d.getTime() < now;
          const isToday = d.toDateString() === new Date().toDateString();
          const when = d.toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
          return (
            <div key={a.activity_id} data-testid={`scheduled-activity-row-${a.activity_id}`}
              className="flex items-center gap-3 p-3 rounded-xl bg-[#18181B] hover:bg-white/[0.03] transition-colors">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: (meta?.accent || "#71717a") + "20", color: meta?.accent }}>
                {Icon && <Icon size={15} />}
              </div>
              <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setSelected(a.lead_id)}>
                <div className="font-medium truncate flex items-center gap-2">{a.lead_nama}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${overdue ? "bg-red-500/15 text-red-400" : isToday ? "bg-amber-500/15 text-amber-400" : "bg-white/5 text-zinc-400"}`}>
                    {overdue ? "Terlambat" : isToday ? "Hari ini" : when.split(",")[0]}
                  </span>
                </div>
                <div className="text-[11px] text-zinc-500">{meta?.label} · {when}{a.note ? ` · ${a.note}` : ""}</div>
              </div>
              {a.meeting_link && (
                <a href={a.meeting_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`scheduled-activity-link-${a.activity_id}`}
                  className="shrink-0 w-8 h-8 rounded-lg bg-emerald-600/20 text-emerald-400 flex items-center justify-center hover:bg-emerald-600/30"><Link2 size={14} /></a>
              )}
              {a.maps_link && (
                <a href={a.maps_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`scheduled-activity-maps-${a.activity_id}`}
                  className="shrink-0 w-8 h-8 rounded-lg bg-purple-600/20 text-purple-400 flex items-center justify-center hover:bg-purple-600/30"><MapPin size={14} /></a>
              )}
              <ChevronRight size={16} className="text-zinc-600 shrink-0 cursor-pointer" onClick={() => setSelected(a.lead_id)} />
            </div>
          );
        }} />
      )}
      <LeadDetailSheet leadId={selected} initialTab="activity" onClose={() => setSelected(null)} />
    </div>
  );
}
